import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminHooksTab = () => {
  const [targets, setTargets] = useState([]);
  const [hooks, setHooks] = useState([]);
  const [form, setForm] = useState({ name: '', kind: 'custom', endpoint_url: '', auth_token: '' });

  const load = async () => {
    try {
      const [t, h] = await Promise.all([
        axios.get(`${API}/admin/platform-targets`, { withCredentials: true }),
        axios.get(`${API}/admin/capability-hooks`, { withCredentials: true }),
      ]);
      setTargets(t.data.targets || []);
      setHooks(h.data.hooks || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const addTarget = async () => {
    if (!form.endpoint_url) return toast.error('Endpoint URL required');
    await axios.post(`${API}/admin/platform-targets`, form, { withCredentials: true });
    setForm({ name: '', kind: 'custom', endpoint_url: '', auth_token: '' });
    toast.success('External runtime registered');
    load();
  };
  const probe = async (id) => {
    const r = await axios.post(`${API}/admin/platform-targets/${id}/probe`, {}, { withCredentials: true });
    toast[r.data.status === 'linked' ? 'success' : 'error'](`Probe: ${r.data.status}${r.data.error ? ' — ' + r.data.error : ''}`);
    load();
  };
  const remove = async (id) => { await axios.delete(`${API}/admin/platform-targets/${id}`, { withCredentials: true }); load(); };

  return (
    <div className="space-y-8" data-testid="admin-hooks-tab">
      <div>
        <h3 className="text-white font-bold mb-1">External Runtime Targets</h3>
        <p className="text-xs text-zinc-500 mb-4">Engine-agnostic capability injection. The local runtime is always the default and the fallback — external runtimes are enhancements. Any conforming runtime connects via /health + /accept-hook endpoints.</p>
        <div className="grid md:grid-cols-4 gap-2 mb-3">
          <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0F0F12] border-[#1E1E24] text-white" data-testid="hooks-target-name" />
          <Input placeholder="Endpoint URL" value={form.endpoint_url} onChange={(e) => setForm({ ...form, endpoint_url: e.target.value })} className="bg-[#0F0F12] border-[#1E1E24] text-white" data-testid="hooks-target-url" />
          <Input placeholder="Auth token (optional)" value={form.auth_token} onChange={(e) => setForm({ ...form, auth_token: e.target.value })} className="bg-[#0F0F12] border-[#1E1E24] text-white" data-testid="hooks-target-token" />
          <Button onClick={addTarget} className="bg-[#06B6D4] text-black" data-testid="hooks-add-target-btn">Register</Button>
        </div>
        <div className="space-y-2">
          {targets.length === 0 && <p className="text-zinc-600 text-sm">No external runtimes linked — engine runs fully standalone.</p>}
          {targets.map((t) => (
            <div key={t.target_id} className="flex items-center justify-between bg-[#0A0A0C] border border-[#1E1E24] rounded-lg px-4 py-3">
              <div>
                <span className="text-white text-sm font-medium">{t.name || 'External Runtime'}</span>
                <span className="text-zinc-500 text-xs ml-3">{t.endpoint_url}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-0.5 rounded-full ${t.status === 'linked' ? 'bg-green-500/20 text-green-300' : t.status === 'disabled' ? 'bg-red-500/20 text-red-300' : 'bg-zinc-500/20 text-zinc-400'}`}>{t.status}</span>
                <Button size="sm" variant="outline" onClick={() => probe(t.target_id)} className="border-[#1E1E24] text-xs" data-testid={`hooks-probe-${t.target_id}`}>Probe</Button>
                <Button size="sm" variant="ghost" onClick={() => remove(t.target_id)} className="text-red-400 text-xs">Delete</Button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div>
        <h3 className="text-white font-bold mb-2">Capability Hooks</h3>
        <div className="space-y-2">
          {hooks.length === 0 && <p className="text-zinc-600 text-sm">No capability hooks emitted yet.</p>}
          {hooks.map((h) => (
            <div key={h.hook_id} className="bg-[#0A0A0C] border border-[#1E1E24] rounded-lg px-4 py-3 text-sm">
              <div className="flex justify-between">
                <span className="text-white">{h.request || h.scope}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${h.status === 'synced' ? 'bg-green-500/20 text-green-300' : 'bg-cyan-500/20 text-cyan-300'}`}>{h.status}</span>
              </div>
              <span className="text-zinc-500 text-xs">project: {h.project_id} · {h.created_at?.slice(0, 19)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const AdminTelemetryTab = () => {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    axios.get(`${API}/telemetry/summary`, { withCredentials: true })
      .then((r) => setRows(r.data.projects || [])).catch(() => {});
  }, []);
  return (
    <div data-testid="admin-telemetry-tab">
      <h3 className="text-white font-bold mb-4">Play Telemetry</h3>
      {rows.length === 0 && <p className="text-zinc-600 text-sm">No play events recorded yet.</p>}
      <div className="space-y-2">
        {rows.map((p) => (
          <div key={p.project_id} className="bg-[#0A0A0C] border border-[#1E1E24] rounded-lg px-4 py-3 flex items-center justify-between">
            <span className="text-white text-sm">{p.name}</span>
            <div className="flex gap-4 text-xs font-mono">
              <span className="text-cyan-300">▶ {p.events.play_started || 0}</span>
              <span className="text-green-300">🏆 {p.events.game_won || 0}</span>
              <span className="text-red-300">💀 {p.events.game_lost || 0}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const AdminDiagnosticsTab = () => {
  const [rows, setRows] = useState([]);
  const load = () => axios.get(`${API}/diagnostics`, { withCredentials: true }).then((r) => setRows(r.data.diagnostics || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => { await axios.put(`${API}/diagnostics/${id}`, { status }, { withCredentials: true }); load(); };
  const sevColor = { critical: 'text-red-400', error: 'text-orange-400', warning: 'text-yellow-400', info: 'text-cyan-400' };
  return (
    <div data-testid="admin-diagnostics-tab">
      <h3 className="text-white font-bold mb-4">Runtime Diagnostics</h3>
      {rows.length === 0 && <p className="text-zinc-600 text-sm">No diagnostics logged.</p>}
      <div className="space-y-2">
        {rows.map((d) => (
          <div key={d.diag_id} className="bg-[#0A0A0C] border border-[#1E1E24] rounded-lg px-4 py-3 flex items-center justify-between text-sm">
            <div>
              <span className={`font-mono text-xs uppercase mr-3 ${sevColor[d.severity] || 'text-zinc-400'}`}>{d.severity}</span>
              <span className="text-white">{d.message}</span>
              <span className="text-zinc-600 text-xs ml-2">{d.source} · {d.status}</span>
            </div>
            {d.status !== 'resolved' && (
              <div className="flex gap-2">
                {d.status === 'open' && (
                  <Button size="sm" variant="outline" className="border-[#1E1E24] text-xs" onClick={() => setStatus(d.diag_id, 'acknowledged')}>Ack</Button>
                )}
                <Button size="sm" variant="outline" className="border-[#1E1E24] text-xs text-green-400" onClick={() => setStatus(d.diag_id, 'resolved')}>Resolve</Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
